<template>
  <b-overlay :show="loading" rounded="lg">
    <div class="playlist-manager">
      <b-card class="filter-card mb-4">
        <b-row class="align-items-center">
            <b-button variant="primary" class="ml-2" @click="openCreateModal">
              <feather-icon icon="PlusIcon"></feather-icon>
              پلی‌لیست جدید
            </b-button>
        </b-row>
      </b-card>

      <!-- Playlist Grid -->
      <b-row v-if="playlists.length > 0">
        <b-col
            v-for="playlist in playlists"
            :key="playlist.items"
            cols="6"
            class="mb-4"
        >
          <PlaylistCard
              :playlist="playlist"
              @edit="openEditModal"
              @delete="confirmDelete"
              @add-item="openAddItemModal"
              @reorder="handleReorder"
              @refresh="fetchPlaylists"
          />
        </b-col>
      </b-row>

      <!-- Empty State -->
      <div v-else class="empty-state py-5 text-center">
        <i class="fas fa-list-ul" style="font-size: 4rem; color: #cbd5e1;"></i>
        <h4 class="mt-3">هیچ پلی‌لیستی یافت نشد</h4>
        <p class="text-muted">برای این موجودیت، پلی‌لیستی تعریف نشده است. اولین پلی‌لیست را ایجاد کنید.</p>
        <b-button variant="primary" @click="openCreateModal">
          <i class="fas fa-plus"></i> ایجاد پلی‌لیست
        </b-button>
      </div>
    </div>

    <!-- Modals -->
    <CreatePlaylistModal
        ref="createModal"
        :owner-type="filters.ownerType"
        :owner-id="filters.ownerId"
        @created="fetchPlaylists"
    />

    <EditPlaylistModal
        ref="editModal"
        :playlist="editingPlaylist"
        @updated="fetchPlaylists"
    />

    <AddItemModal
        ref="addItemModal"
        :playlist="targetPlaylist"
        @added="fetchPlaylists"
    />
  </b-overlay>
</template>

<script>
import {BButton, BCard, BCol, BFormGroup, BFormInput, BFormSelect, BOverlay, BRow} from 'bootstrap-vue'
import {PlaylistDeleteRequest, PlaylistGetByOwnerRequest} from '@/libs/Api/Playlist'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import AddItemModal from "@/views/components/Playlist/AddItemModal.vue";
import EditPlaylistModal from "@/views/components/Playlist/EditPlaylistModal.vue";
import CreatePlaylistModal from "@/views/components/Playlist/CreatePlaylistModal.vue";
import PlaylistCard from "@/views/components/Playlist/PlaylistCard.vue";

export default {
  name: 'PlaylistManager',
  components: {
    PlaylistCard,
    CreatePlaylistModal,
    EditPlaylistModal,
    AddItemModal,
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BButton,
  },
  data() {
    return {
      loading: false,
      playlists: [],
      filters: {
        ownerType: 1, // default: Product
        ownerId: this.$route.params.id
      },
      ownerTypeOptions: [
        {value: 1, text: 'محصول'},
        {value: 2, text: 'وبلاگ'},
        {value: 3, text: 'پست'},
        {value: 4, text: 'صفحه'},
        {value: 5, text: 'پروفایل کاربر'},
        {value: 6, text: 'پادکست'}
      ],
      editingPlaylist: null,
      targetPlaylist: null
    }
  },
  created() {
    // Try to get ownerType and ownerId from route params or query
    if (this.$route.query.ownerType) {
      this.filters.ownerType = parseInt(this.$route.query.ownerType)
    }
    if (this.$route.query.ownerId) {
      this.filters.ownerId = parseInt(this.$route.query.ownerId)
    }
    this.fetchPlaylists()
  },
  methods: {
    fetchPlaylists() {
      this.loading = true
      const request = new PlaylistGetByOwnerRequest(this)
      request.setParams({
        ownerType: this.filters.ownerType,
        ownerId: this.filters.ownerId
      })
      request.fetch(
          (content) => {
            this.playlists = content || []
            this.loading = false
          },
          (error) => {
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-center',
              props: {
                title: 'خطا در بارگذاری',
                icon: 'XIcon',
                variant: 'danger',
                text: error.message || 'مشکل در دریافت پلی‌لیست‌ها'
              }
            })
          }
      )
    },
    resetFilters() {
      this.filters.ownerType = 1
      this.filters.ownerId = 1
      this.fetchPlaylists()
    },
    openCreateModal() {
      this.$bvModal.show('modal-create-playlist')
    },
    openEditModal(playlist) {
      this.editingPlaylist = playlist
      this.$bvModal.show('modal-edit-playlist')
    },
    openAddItemModal(playlist) {
      this.targetPlaylist = playlist
      this.$bvModal.show('modal-item-playlist')
    },
    confirmDelete(playlist) {
      this.$bvModal.msgBoxConfirm(
          `آیا از حذف پلی‌لیست "${playlist.title}" اطمینان دارید؟ این عمل غیرقابل بازگشت است.`,
          {
            title: 'تأیید حذف',
            okVariant: 'danger',
            okTitle: 'حذف',
            cancelTitle: 'لغو',
            headerBgVariant: 'transparent',
            headerTextVariant: 'light'
          }
      ).then(confirmed => {
        if (confirmed) {
          this.deletePlaylist(playlist.id)
        }
      })
    },
    deletePlaylist(playlistId) {
      this.loading = true
      const request = new PlaylistDeleteRequest(this)
      request.setParams({playlistId})
      request.fetch(
          () => {
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-center',
              props: {
                title: 'موفق',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'پلی‌لیست با موفقیت حذف شد.'
              }
            })
            this.fetchPlaylists()
          },
          (error) => {
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-center',
              props: {
                title: 'خطا در حذف',
                icon: 'XIcon',
                variant: 'danger',
                text: error.message || 'مشکل در حذف پلی‌لیست'
              }
            })
          }
      )
    },
    handleReorder({playlistId, order}) {
      // This is handled inside PlaylistCard via reorder method
      // Just refresh the list after reorder
      this.fetchPlaylists()
    }
  }
}
</script>

<style scoped>
.filter-card {
  border-radius: 18px;
  border: 1px solid #e2e8f0;
}

.empty-state {
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  padding: 3rem;
}
</style>
